import { createReducer } from "reduxsauce"
import actionCreator from "helpers/actionCreator"
import { filter, findIndex, take, forEach, get, isEmpty, some, size, toLower, map } from "lodash"
import { takeLatest, select, put, call, getContext, all } from "redux-saga/effects"
import { del as delApi } from "api"
import moment from "moment"
import { sendQuery } from "api/graphql"
import { Types as LoaderTypes, Creators as modelCreators } from "model/data"
import { Types as ServerNotificationsTypes } from "modules/server-notifications/model"
import { Types as BaseTypes } from "modules/portfolio-root/model"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Creators as SegmentCreators } from "modules/compare-segments-root/model"
import { Creators as EditExperimentCreators } from "modules/edit-experiment/model"
import { Creators as DialogCreators } from "modules/dialog/model"
import { getUserPreference } from "helpers/userPreferences"
import { getEarliestDate, getLatestDate } from "./selectors/dates"
import { getPortfolioId } from "../portfolio-root/selectors/portfolios"
import { getIsOpPro } from "modules/account-root/selectors"
import { getComparisonItems, getCurrentPositionSet, getRawComparisonItems, getSelectedSettings } from "./selectors"
import { updateUploadingExperiments, updateAlternativeExperimentProgress } from "helpers/comparison"
import { getEditExperimentUrl } from "./helpers"

function updatePreferences(state) {
  return {
    type: "experimentSettings",
    value: getSelectedSettings(state),
    key: getPortfolioId(state),
  }
}

export const { Types, Creators } = actionCreator("ompnt/modules/experiments/", {
  removeComparisonItem: {
    props: ["item"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  trackExperimentTypeSelection: {
    props: ["experimentType"],
    track: true,
    trackType: "experiments-experiment-type-selected",
    trackPayload: ({ experimentType }) => ({ type: experimentType }),
  },
  trackPanelTrigger: {
    props: ["panel"],
    track: true,
    trackType: "experiments-accordion-triggered",
    trackPayload: ({ panel }) => ({ accordion: panel }),
  },
  addComparisonItem: {
    props: ["item"],
    track: true,
    trackType: "experiments-experiment-added",
    trackPayload: {},
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  setSelectedComparisonType: {
    props: ["selectedComparisonType"],
    track: true,
    trackType: "experiments-compare-tab-switch",
    trackPayload: ({ selectedComparisonType }) => ({ tab: selectedComparisonType }),
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  setFrontierDate: {
    props: ["frontierDate"],
    track: true,
    trackType: "experiments-frontier-date-selected",
    trackPayload: ({ frontierDate }) => ({ date: frontierDate.adjusted || frontierDate.selectedDate }),
  },
  loadSavedComparison: ["compareSettings"],
  loadComparison: ["comparisonItems"],
  saveMetricToStore: {
    props: ["metric", "comparisonType"],
    track: true,
    trackType: "experiments-graph-data-selected",
    trackPayload: ({ metric }) => ({ selected: metric }),
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  saveCompositionGroupToStore: {
    props: ["group"],
    track: true,
    trackType: "experiments-composition-group-selected",
    trackPayload: ({ group }) => ({ group }),
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  removeNonOverlappingItemsSuccess: {
    props: ["comparisonItems"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  removeNonOverlappingItems: ["items"],
  loadNewExperiment: ["newExperiment"],
  preLoadExperiment: ["experiment"],
  setComparisonItems: {
    props: ["items"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  toggleTradesPanel: ["experiment"],
  evaluateAs: {
    props: ["item"],
    track: true,
    trackType: "experiments-experiment-evaluate-as",
    trackPayload: {},
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  toggleRenameExperiment: ["experiment"],
  toggleUpdateExperiment: {
    props: ["experiment"],
    track: true,
    trackType: "experimets-update-experiment-btn",
  },
  deleteExperiment: ["experiment"],
  deleteExperimentSuccess: [],
  deleteExperimentError: [],
  renameExperiment: ["experiment"],
  editExperiment: ["experiment"],
  openDeleteDialog: [],
  trackSetPeriod: {
    props: ["period"],
    track: true,
    trackType: "experiments-date-period-updated",
    trackPayload: ({ period }) => ({ period }),
  },
  loadSnapshot: ["payload"],
  setSnapshot: ["snapshot"],
  convertExperiment: ["experiment", "portfolio", "showPortfolio"],
  toggleCreatePortfolio: ["experiment"],
  toggleFxHedge: {
    props: ["item"],
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
})

const DEFAULT_STATE = {
  tradesPanelOpen: false,
  comparisonItems: [],
  selectedComparisonType: { id: "performance" },
  savedMetrics: {},
  savedCompositionGroup: "",
  selected: null,
  frontierDate: null,
  renameExperimentOpen: false,
  experimentToUpdate: null,
  snapshot: null,
  newPortfolio: null,
  experimentToConvert: null,
}

function handleEvaluateAs(state, { item: { id, duplicate, evaluateAs } }) {
  const itemIndex = findIndex(state.comparisonItems, elem => elem.id === id && elem.duplicate === duplicate)
  const comparisonItems = [...state.comparisonItems]
  comparisonItems[itemIndex].evaluateAs = evaluateAs === "long" ? "short" : "long"
  return { ...state, comparisonItems }
}

function handleRemoveNonOverlappingItemsSuccess(state, { comparisonItems }) {
  return { ...state, comparisonItems }
}

function handleAddComparisonItem(state, { item }) {
  const { comparisonItems } = state
  if (some(comparisonItems, ({ id }) => id === item.id)) {
    return state
  }
  const newComparisonItems = [...comparisonItems, item]
  return { ...state, comparisonItems: newComparisonItems }
}

function handleSetComparisonItems(state, { items }) {
  return { ...state, comparisonItems: filter(items, ({ initialItem }) => !initialItem) }
}

export function handleSetFrontierDate(state, { frontierDate }) {
  return { ...state, frontierDate: frontierDate.adjusted || frontierDate.selectedDate }
}

function handleSetSelectedComparisonType(state, { selectedComparisonType }) {
  return { ...state, selectedComparisonType }
}

function handleSaveMetricToStore(state, { metric, comparisonType }) {
  const metrics = state.savedMetrics ? { ...state.savedMetrics } : {}

  metrics[comparisonType] = metric

  return { ...state, savedMetrics: metrics }
}

function handleSaveCompositionGroupToStore(state, { group }) {
  return { ...state, savedCompositionGroup: group }
}

function handleToggleTradesPanel(state, { experiment }) {
  const { tradesPanelOpen, selected } = state

  if (!experiment) {
    return {
      ...state,
      tradesPanelOpen: false,
      selected: null,
    }
  }

  if (tradesPanelOpen && selected) {
    if (selected.id !== experiment.id) {
      return { ...state, selected: experiment }
    }
  } else {
    return {
      ...state,
      tradesPanelOpen: !state.tradesPanelOpen,
      selected: experiment,
    }
  }

  return state
}

function handleLoadSavedComparison(state, { compareSettings }) {
  let comparisonItems = []
  let selectedComparisonType = { id: "performance" }
  let savedMetrics = {}
  let savedCompositionGroup = "SECTOR"

  if (compareSettings) {
    if (compareSettings.selectedComparisonType) {
      selectedComparisonType = compareSettings.selectedComparisonType
    }

    if (compareSettings.savedMetrics) {
      savedMetrics = compareSettings.savedMetrics
    }

    if (compareSettings.comparisonItems) {
      // Converted 'SWAP' type to 'swap', need to handle saved preference
      forEach(compareSettings.comparisonItems, elem => {
        elem.type = toLower(elem.type)
      })
      comparisonItems = compareSettings.comparisonItems
    }

    if (compareSettings.savedCompositionGroup) {
      savedCompositionGroup = compareSettings.savedCompositionGroup
    }
  }

  return {
    ...state,
    selectedComparisonType,
    savedMetrics,
    comparisonItems,
    savedCompositionGroup,
    tradesPanelOpen: false,
    selected: null,
  }
}

function handleLoadComparison(state, { comparisonItems }) {
  comparisonItems = take(comparisonItems, 4)
  return {
    ...state,
    comparisonItems,
    tradesPanelOpen: false,
    selected: null,
  }
}

function handleSetModel(state) {
  return { ...state, tradesPanelOpen: false, selected: null, frontierDate: null }
}

function handleDeleteExperimentSuccess(state, experimentId) {
  return { ...state, experimentId }
}

function handleToggleRenameExperiment(state, { experiment }) {
  return {
    ...state,
    renameExperimentOpen: !state.renameExperimentOpen,
    experimentToUpdate: {
      ...experiment,
      name: experiment.label,
    },
  }
}

function handleToggleCreatePortfolio(state, { experiment }) {
  return {
    ...state,
    experimentToConvert: {
      ...experiment,
      datetime: moment(),
    },
  }
}

function handleSetSnapshot(state, { snapshot }) {
  return {
    ...state,
    snapshot,
  }
}

function handleLoadSnapshot(state, { payload }) {
  return {
    ...state,
    comparisonItems: get(payload, "items", []),
    tradesPanelOpen: false,
    selected: null,
    snapshot: null,
  }
}

function handleToggleFxHedge(state, { item }) {
  const comparisonItems = map(state.comparisonItems, (comparisonItem) => {
    if (comparisonItem.id === item.id) {
      return { ...comparisonItem, fxHedgeEnabled: !comparisonItem.fxHedgeEnabled }
    }
    return comparisonItem
  })

  return { ...state, comparisonItems }
}

export default createReducer(DEFAULT_STATE, {
  [Types.ADD_COMPARISON_ITEM]: handleAddComparisonItem,
  [Types.SET_COMPARISON_ITEMS]: handleSetComparisonItems,
  [Types.SET_FRONTIER_DATE]: handleSetFrontierDate,
  [Types.SET_SELECTED_COMPARISON_TYPE]: handleSetSelectedComparisonType,
  [Types.LOAD_SAVED_COMPARISON]: handleLoadSavedComparison,
  [Types.LOAD_COMPARISON]: handleLoadComparison,
  [Types.SAVE_METRIC_TO_STORE]: handleSaveMetricToStore,
  [Types.SAVE_COMPOSITION_GROUP_TO_STORE]: handleSaveCompositionGroupToStore,
  [Types.REMOVE_NON_OVERLAPPING_ITEMS_SUCCESS]: handleRemoveNonOverlappingItemsSuccess,
  [Types.TOGGLE_TRADES_PANEL]: handleToggleTradesPanel,
  [BaseTypes.SET_MODEL]: handleSetModel,
  [Types.EVALUATE_AS]: handleEvaluateAs,
  [Types.DELETE_EXPERIMENT_SUCCESS]: handleDeleteExperimentSuccess,
  [Types.TOGGLE_RENAME_EXPERIMENT]: handleToggleRenameExperiment,
  [Types.LOAD_SNAPSHOT]: handleLoadSnapshot,
  [Types.SET_SNAPSHOT]: handleSetSnapshot,
  [Types.TOGGLE_CREATE_PORTFOLIO]: handleToggleCreatePortfolio,
  [Types.TOGGLE_FX_HEDGE]: handleToggleFxHedge,
})

function* handleLoadSuccess(action) {
  const { comparisonItems, currentPortfolio, snapshot } = yield select(state => ({
    comparisonItems: state.modules.experiments.comparisonItems,
    currentPortfolio: get(state, "global.selected.portfolio"),
    snapshot: get(state, "modules.experiments.snapshot"),
  }))

  if (action.resource === "portfolio") {
    const portfolioId = get(action, "data.id")
    if (currentPortfolio && portfolioId !== currentPortfolio) {
      yield put(Creators.loadSavedComparison({ comparisonItems: [] }))
    }
  } else if (action.resource === "experimentsPreferences") {
    // comparison items are flushed on model change, in the event a comparisonItems pre-load occurs, don't override that pre-load with pref
    if (size(comparisonItems) === 0) {
      const { portfolioExperiments } = yield select(state => ({
        portfolioExperiments: get(state, "global.data.portfolioExperiments.data.portfolio.experiments", []),
      }))

      const preferedExperimentSettings = getUserPreference(action.data, "experimentSettings", null)
      if (preferedExperimentSettings?.comparisonItems) {
        preferedExperimentSettings.comparisonItems = filter(preferedExperimentSettings.comparisonItems, compareItem => compareItem.type !== "experiment" || some(portfolioExperiments, elem => elem.id === compareItem.id))
      }
      if (snapshot) {
        yield put(Creators.loadSnapshot(snapshot))
      } else {
        yield put(Creators.loadSavedComparison(preferedExperimentSettings))
      }
    }
  } else if (action.resource === "notifications" && !isEmpty(action.data)) {
    const newComparisonItems = updateUploadingExperiments(comparisonItems, action.data)

    yield put(Creators.setComparisonItems(newComparisonItems))
  }
}

function* handleRemoveNonOverlappingItems({ items }) {
  const { comparisonItems } = yield select(state => ({
    comparisonItems: state.modules.experiments.comparisonItems,
  }))
  const newComparisonItems = filter(comparisonItems, item => findIndex(items, { id: item.id }) === -1)

  yield put(notificationCreators.add("error", null, `${items.map(item => item.label).join(", ")} cannot be loaded because there are no overlapping dates with the current compare selection.`))
  yield put(Creators.removeNonOverlappingItemsSuccess(newComparisonItems))
}

function* handleLoadNewExperiment({ newExperiment }) {
  const [comparisonItems, earliestDate, latestDate, { fxHedgeEnabled }] = yield all([
    select(getRawComparisonItems),
    select(getEarliestDate),
    select(getLatestDate),
    select(getCurrentPositionSet),
  ])

  if (!some(comparisonItems, elem => elem.id === newExperiment.id)) {
    // If we don't have the maximum number of comparison items. (1 base item + 4 comparisons)
    if (comparisonItems.length < 4) {
      // If we have an experiment date and it is outside the current experiments date range.
      if (newExperiment.date && (newExperiment.date < earliestDate || newExperiment.date > latestDate)) {
        yield put(notificationCreators.add("info", null, `${newExperiment.name} dates have been adjusted to reflect data availability of other loaded experiments. To access ${newExperiment.name}'s full history, please remove other experiments.`))
      }
      // Use setComparisonItems because if you use addComparisonItems, the components will get reloaded before the componentDidUpdate setParentComparisonItems function is called
      yield put(Creators.setComparisonItems([...comparisonItems, { ...newExperiment, fxHedgeEnabled }]))
    } else {
      yield put(notificationCreators.add("info", null, `${newExperiment.name} could not be loaded. Please remove an experiment from a column. This (and all saved experiments) can be selected by "Searching for an Existing Experiment" from the "Add Experiment" dialog.`))
    }
  }
}

function* handlePreLoadExperiment({ experiment }) {
  const { comparisonItems } = yield select(state => ({
    comparisonItems: state.modules.experiments.comparisonItems,
  }))

  const itemExist = some(comparisonItems, elem => elem.id === experiment.id)
  if (!itemExist) {
    // If we don't have the maximum number of comparison items. (1 base item + 4 comparisons)
    if (comparisonItems.length < 4) {
      yield put(Creators.addComparisonItem({ ...experiment, type: "experiment", uploading: true }))
    } else {
      yield put(notificationCreators.add("info", null, `${experiment.name} could not be added. Please remove an experiment from a column. This (and all saved experiments) can be selected by "Searching for an Existing Experiment" from the "Add Experiment" dialog.`))
    }
  }
}

function* handleUploadUpdate({ state: { updatedNotifications } }) {
  const { comparisonItems } = yield select(state => ({
    comparisonItems: state.modules.experiments.comparisonItems,
  }))

  const newComparisonItems = updateAlternativeExperimentProgress(comparisonItems, updatedNotifications)

  // do not update the items if nothing was updated
  if (newComparisonItems) {
    yield put(Creators.setComparisonItems(newComparisonItems))
    yield put(modelCreators.resetCollections(["portfolioExperiments", "experiments"]))
  }
}

function* handleToggleUpdateExperiment({ experiment }) {
  const { account, comparisonItems } = yield select(state => ({
    account: state.global.selected.account,
    comparisonItems: getComparisonItems(state),
  }))

  if (experiment.experimentType !== "PORTFOLIO") {
    const index = findIndex(comparisonItems, ({ id }) => id === experiment.id)
    yield put(EditExperimentCreators.loadIndex(index))
  }

  const { navigate } = yield getContext("router")
  yield call(navigate, getEditExperimentUrl(account, experiment))
}

function* startDeleteExperiment({ experiment }) {
  const { account } = yield select(state => ({
    account: state.global.selected.account,
  }))

  const { id, experimentType, portfolioId } = experiment

  const mutation = `mutation ($portfolioId: String!, $experimentId: String!) {
      deleteExperiment (portfolioId: $portfolioId, experimentId: $experimentId) { ok }
    }`

  try {
    let status
    if (experimentType === "OPTIMIZATION") {
      yield call(delApi, `/api/${account}/e/${id}`)
      status = true
    } else {
      const { data } = yield call(sendQuery, mutation, { experimentId: id, portfolioId }, account)
      status = get(data, "deleteExperiment.ok")
    }

    if (status) {
      yield put(SegmentCreators.removeComparisonItem(experiment))
      yield put(Creators.deleteExperimentSuccess(id))
      yield put(modelCreators.resetCollections(["portfolioExperiments", "experiments"]))
      yield put(notificationCreators.add("success", null, "Experiment successfully deleted."))
    } else {
      yield put(Creators.deleteExperimentError())
      yield put(notificationCreators.add("error", null, "An error happened while deleting your experiment."))
    }
  } catch (err) {
    yield put(Creators.deleteExperimentError(err))
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while deleting your experiment."))
  }
}

function* getPositionSetFromExperiment(account, experiment) {
  const query = `query ($experimentId: String!, $portfolioId: String!, $date: Date!) {
    portfolio(id: $portfolioId) {
      experiment(id: $experimentId) {
        dates(from: $date to: $date skipSwapSplit: true){
          date
          equity
          equities{
            id{
              ticker
              mic
              bloombergTicker
              modelProviderId
              internalId
            }
            economicExposure
          }
          currencies{
            id
            economicExposure
          }
          swaps{
            id
            economicExposure
          }
          fixedIncome{
            id{
              isin
            }
            economicExposure
          }
          commodities{
            id
            economicExposure
          }
          indexes{
            id
            economicExposure
          }
          otherAssets{
            id
            economicExposure
          }
        }
      }
    }
  }`
  const variables = {
    experimentId: experiment.id,
    date: experiment.availableFrom,
    portfolioId: experiment.portfolioId,
  }

  const { errors, apiMessages, data } = yield call(sendQuery, query, variables, account)
  const positionSet = get(data, "portfolio.experiment.dates.0")

  if (errors || get(apiMessages, "errors") || !positionSet) {
    throw new Error()
  }
  return positionSet
}

function* createPortfolio(account, portfolio, isOpPro) {
  const { name, live, description, modelId, intraday, betaInstrument } = portfolio
  const mutation = `mutation ($portfolio: NewPortfolio!) {
    createPortfolio (portfolio: $portfolio) {
      id,
      description,
      name,
      alias,
      defaultModelId,
      availableFrom,
      lastUpdated,
      rolloverPositionSetToCurrentDate,
      ${isOpPro ? "" : "intradayEnabled"}
     }
  }`
  const variables = {
    portfolio: {
      name,
      rolloverPositionSetToCurrentDate: !!live,
      defaultModelId: modelId,
      description,
      ...(isOpPro ? {} : { intradayEnabled: intraday }),
      ...(!isOpPro && betaInstrument ? { betaInstrument } : {}),
    },
  }
  const { errors, apiMessages, data } = yield call(sendQuery, mutation, variables, account)
  const portfolioResult = get(data, "createPortfolio")

  if (errors || get(apiMessages, "errors") || !portfolioResult) {
    throw new Error()
  }
  return portfolioResult
}

function* uploadPositionSet(account, portfolioId, positionSet) {
  const mutation = `mutation ($portfolioId: String!, $positionSet: PositionSetDateInput!) {
    uploadPositionSetDate (portfolioId: $portfolioId, data: $positionSet) { ok }
  }`
  const variables = {
    portfolioId,
    positionSet,
  }

  const { errors, apiMessages, data } = yield call(sendQuery, mutation, variables, account)
  const ok = get(data, "uploadPositionSetDate.ok")

  if (errors || get(apiMessages, "errors") || !ok) {
    throw new Error()
  }
  return ok
}

function* convertExperiment(args) {
  const { experiment, portfolio, showPortfolio } = args

  const { account, isOpPro } = yield select(state => ({
    account: state.global.selected.account,
    isOpPro: getIsOpPro(state),
  }))

  try {
    const positionSet = yield getPositionSetFromExperiment(account, experiment)
    const newPortfolio = yield createPortfolio(account, portfolio, isOpPro)
    yield uploadPositionSet(account, newPortfolio.id, positionSet)

    if (showPortfolio) {
      yield put(DialogCreators.close())
      const { navigate } = yield getContext("router")
      yield call(navigate, `/${account}/p/${newPortfolio.alias}`)
    } else {
      yield put(notificationCreators.add("info", null, `New portfolio successfully created. You can now access it using the following alias: ${newPortfolio.alias}`))
    }

    yield put(modelCreators.graphqlCreateSuccess("accountPortfolios", "account-portfolios", "portfolios", { ...newPortfolio, availableFrom: experiment.availableFrom }))
  } catch {
    // We don't need information from the error object because the error message is the same for all of them
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while converting the experiment to a portfolio."))
  }
}

export function* saga() {
  yield takeLatest(LoaderTypes.LOAD_SUCCESS, handleLoadSuccess)
  yield takeLatest(ServerNotificationsTypes.UPDATE_SUCCESS, handleUploadUpdate)
  yield takeLatest(Types.REMOVE_NON_OVERLAPPING_ITEMS, handleRemoveNonOverlappingItems)
  yield takeLatest(Types.LOAD_NEW_EXPERIMENT, handleLoadNewExperiment)
  yield takeLatest(Types.PRE_LOAD_EXPERIMENT, handlePreLoadExperiment)
  yield takeLatest(Types.DELETE_EXPERIMENT, startDeleteExperiment)
  yield takeLatest(Types.TOGGLE_UPDATE_EXPERIMENT, handleToggleUpdateExperiment)
  yield takeLatest(Types.CONVERT_EXPERIMENT, convertExperiment)
}
